/**
 * Live Google reviews via Places API (New).
 *
 * ─────────────────────────────────────────────────────────────
 * WHY places.googleapis.com AND NOT maps.googleapis.com
 * ─────────────────────────────────────────────────────────────
 * The legacy Place Details web service does not send CORS headers — a
 * browser fetch against it dies before it leaves DevTools. Places API (New)
 * does support CORS, takes the key as a query param, and returns the same
 * five Google-chosen reviews. Same key as the map embed; the API must be
 * enabled on the project and allowed on the key.
 *
 * Three states, as specced: skeletons while loading, live cards on success,
 * and a link out to Google on any failure. An empty google_place_id short-
 * circuits straight to the fallback with no network call and no console
 * noise — that is today's state until the API is enabled in GCP and the ID
 * is added to shop.config.json.
 *
 * The response is cached in sessionStorage per locale (Spanish gets Spanish
 * relative dates, and one cache would leak English strings into /es). The
 * old hardcoded quotes stay dead; their copy keys remain per the add-only
 * rule.
 */

import { useEffect, useState } from 'react';
import { useCopy, useLocale } from '../../content';
import { useShopConfig, formatCount } from '../../hooks/useShopConfig';
import { BrassRule } from './BrassRule';
import Carousel from './Carousel';
import { Icon } from '../icons';

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
const CACHE_KEY = 'kd_google_reviews_v1';

interface GoogleReview {
  rating: number;
  text?: { text?: string };
  authorAttribution?: { displayName?: string };
  relativePublishTimeDescription?: string;
}
interface PlaceData {
  rating?: number;
  userRatingCount?: number;
  reviews?: GoogleReview[];
}
type State = { status: 'loading' } | { status: 'error' } | { status: 'ready'; data: PlaceData };

function readCache(locale: string): PlaceData | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw)[locale] ?? null) : null;
  } catch {
    return null;
  }
}
function writeCache(locale: string, data: PlaceData): void {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[locale] = data;
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable — live without the cache */
  }
}

function Stars({ rating }: { rating: number }) {
  const filled = Math.round(rating);
  return (
    <span className="flex gap-0.5" role="img" aria-label={`${rating}/5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < filled ? 'text-gold' : 'text-navy/15'}>
          <Icon name="star" size={14} />
        </span>
      ))}
    </span>
  );
}

const truncate = (s: string, n = 200) => (s.length > n ? `${s.slice(0, n).trimEnd()}…` : s);

export default function ReviewsBlock() {
  const c = useCopy().home.reviews;
  const { t, shop } = useShopConfig();
  const locale = useLocale();
  const [state, setState] = useState<State>({ status: 'loading' });

  const placeId = shop.google_place_id;

  useEffect(() => {
    if (!placeId || !MAPS_KEY) {
      // Nothing to fetch against — today's state until the Places API is
      // enabled and the ID recorded. Fallback, silently.
      setState({ status: 'error' });
      return;
    }
    const cached = readCache(locale);
    if (cached) {
      setState({ status: 'ready', data: cached });
      return;
    }
    let cancelled = false;
    fetch(
      `https://places.googleapis.com/v1/places/${placeId}` +
        `?fields=rating,userRatingCount,reviews&languageCode=${locale}&key=${MAPS_KEY}`
    )
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: PlaceData) => {
        if (cancelled) return;
        writeCache(locale, data);
        setState({ status: 'ready', data });
      })
      .catch((err) => {
        console.warn('ReviewsBlock: Places fetch failed, using fallback', err?.message);
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [placeId, locale]);

  const googleUrl = placeId
    ? `https://search.google.com/local/reviews?placeid=${placeId}`
    : shop.google_maps_url;

  return (
    <section className="bg-page px-6 py-16 md:px-12 md:py-24">
      <div className="mx-auto max-w-6xl">
        <p className="font-oswald text-[12px] uppercase tracking-[0.24em] text-warm-muted">
          {t(c.kicker)}
        </p>
        <BrassRule width={48} className="mt-3 mb-6" />

        {state.status === 'ready' && (
          <>
            <h2 className="font-newsreader text-[34px] font-medium leading-[1.1] text-navy md:text-[46px]">
              {t(c.headline, {
                combinedReviewTotal: formatCount(state.data.userRatingCount ?? 0),
                combinedRating: state.data.rating ?? '',
              })}
            </h2>
            <p className="mt-3 font-newsreader text-[18px] text-slate-body">
              {t(c.sublineGoogle)}
            </p>
            <div className="mt-10">
              <Carousel label={t(c.kicker)}>
                {(state.data.reviews ?? []).map((r, i) => (
                  <figure
                    key={i}
                    className="flex w-[85%] shrink-0 snap-start flex-col gap-3 rounded-card border border-edge-1 bg-white p-6 sm:w-[45%] lg:w-[31%]"
                  >
                    <Stars rating={r.rating} />
                    <blockquote className="font-newsreader text-[16px] leading-[1.6] text-navy/85">
                      {truncate(r.text?.text ?? '')}
                    </blockquote>
                    <figcaption className="mt-auto flex items-baseline justify-between gap-3 pt-2">
                      <span className="font-newsreader text-[15px] font-medium text-navy">
                        {r.authorAttribution?.displayName ?? ''}
                      </span>
                      <span className="font-oswald text-[10px] uppercase tracking-[0.18em] text-warm-muted">
                        {r.relativePublishTimeDescription ?? ''} · Google
                      </span>
                    </figcaption>
                  </figure>
                ))}
              </Carousel>
            </div>
          </>
        )}

        {state.status === 'loading' && (
          <div className="mt-10 flex gap-5 overflow-hidden">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-[180px] w-[85%] shrink-0 animate-pulse rounded-card border border-edge-1 bg-white/60 sm:w-[45%] lg:w-[31%]"
              />
            ))}
          </div>
        )}

        {state.status === 'error' && (
          <>
            <h2 className="font-newsreader text-[34px] font-medium leading-[1.1] text-navy md:text-[46px]">
              {t(c.errorFallbackHeadline)}
            </h2>
            <a
              href={googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 border-b border-gold pb-1 font-oswald text-[13px] font-medium uppercase tracking-[0.2em] text-navy transition-colors hover:text-gold"
            >
              {t(c.errorFallbackCta)}
              <span aria-hidden>→</span>
            </a>
          </>
        )}
      </div>
    </section>
  );
}
